/* eslint-disable react-func/max-lines-per-function */
/* eslint-disable max-lines */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'antd-mobile';
import { useGetState, useMemoizedFn, useUnmount, useUpdateEffect } from 'ahooks';
import { klona } from 'klona';
import { Skeleton } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import { convertObjectKeysToCamelCase, FrameAnimation } from 'common-package';
import { useSelector } from 'react-redux';
import { Icon } from '@fuxi/eevee-icon';
import { selectCurrentServingOrder } from '@/store/service';
import {
  Body2DViewer,
  HumanDetection,
  ServiceProcessCrumb,
  SlideSelect,
  Stepper,
  SuggestionsForFeedback,
  When,
} from '@/components';
import { base64String2ObjectURL, Toast, uploadBase64Img } from '@/utils';
import { SocketClientType } from '@/service/socketio';
import service from '@/service';
import { genMinMaxSteps } from '@/components/stepper';
import type { RefAttrs as Body2DViewerRef } from '@/components/body-2d-viewer';
import type { RefAttrs as HumanDetectionRef } from '@/components/human-detection';
import { storagePrefixFixedV2 } from '@/constant';
import { MassageControlValueContext, PalpationEventBus } from '../index';
import DragContent from './drag-content';
import AnomalyDetectCard from './anomaly-detect-card';
import AnomalyDepthDrawer from './anomaly-depth-drawer';
import CustomDrawLine, { CUSTOM_LINE_COLOR } from './custom-draw-line';
import AcupointCalibration from './acupoint-calibration';

import cx from './index.module.scss';

interface Props {
  socketInstance: React.MutableRefObject<SocketClientType>;
  isSocketConnected: boolean;
  isStoping: boolean;
  endService: () => Promise<boolean>;
  onSuccessPalpation: VoidFunc;
  isDisconnectMachineArm: boolean;
  lineHits: number;
}

const VIDEO_COPYWRITING_DESC: Record<
  Service.BodyPart,
  { firstTips?: string; secondTips?: string; poster?: string; src?: string; tipsTime?: number }
> = {
  Shoulder: {
    firstTips: '请引导用户处于屏幕中央位置，保持静止状态，完全裸露脖子。',
    secondTips: '请确保理疗区域无异物和遮挡，衣物平整无褶皱，保持光照均匀。',
    poster: storagePrefixFixedV2 + '/human-body-detection/detection-poster/shoulder-min.png',
    src: storagePrefixFixedV2 + '/human-body-detection/detection-video/shoulder-video.mp4',
    tipsTime: 5,
  },
  Waist: {
    firstTips: '请引导用户处于屏幕中央位置，保持静止状态，完全裸露脖子。',
    secondTips: '请确保理疗区域无异物和遮挡，衣物平整无褶皱，保持光照均匀。',
    poster: storagePrefixFixedV2 + '/human-body-detection/detection-poster/waist-min.png',
    src: storagePrefixFixedV2 + '/human-body-detection/detection-video/waist-video.mp4',
    tipsTime: 5.1,
  },
  Back: {
    firstTips: '请引导用户处于屏幕中央位置，保持静止状态，完全裸露脖子。',
    secondTips: '请确保理疗区域无异物和遮挡，衣物平整无褶皱，保持光照均匀。',
    poster: storagePrefixFixedV2 + '/human-body-detection/detection-poster/back-min.png',
    src: storagePrefixFixedV2 + '/human-body-detection/detection-video/back-video.mp4',
    tipsTime: 5.1,
  },
  Abdomen: {
    firstTips: '请引导用户处于屏幕中央位置，双手放在身体两侧，保持静止状态。',
    secondTips: '请确保理疗区域无异物和遮挡，衣物平整无褶皱，保持光照均匀。',
    poster: storagePrefixFixedV2 + '/human-body-detection/detection-poster/abdomen-min.png',
    src: storagePrefixFixedV2 + '/human-body-detection/detection-video/abdomen-video.mp4',
    tipsTime: 4.9,
  },
  LowerBody: {},
  All: {},
};

// 裸背检测异常需要特定处理
export const BareAreaAbnormalErrorList: MassageRuntime.RobotExceptionEvents[] = [
  'LIMITED_ABDOMEN_EXPOSED_AREA',
  'LIMITED_SHOULDER_EXPOSED_AREA',
  'LIMITED_WAIST_EXPOSED_AREA',
  'LIMITED_BACK_EXPOSED_AREA',
];
type MassageConfigInfoType = Record<'massage.tech' | 'massage.nodules.duration', number>;

const NODULE_GUIDE_VIDEO = storagePrefixFixedV2 + '/videos/h264/nodule-guide.mp4';
export const GUIDE_VIDEO_POSTER = storagePrefixFixedV2 + '/image/guide-video-poster-min.png';

const Palpation: React.FC<Props> = ({
  socketInstance,
  isSocketConnected,
  isStoping,
  endService,
  onSuccessPalpation,
  /** 机械臂是否断开 */
  isDisconnectMachineArm,
  lineHits,
}) => {
  const { massageState } = useContext(MassageControlValueContext);
  const [leftPoints, setLeftPoints] = useState<Palpation.PointTypeWithDirection[]>([]);
  const [rightPoints, setRightPoints] = useState<Palpation.PointTypeWithDirection[]>([]);
  const [bodyDetectAbnormalPoints, setBodyDetectAbnormalPoints, getBodyDetectAbnormalPoints] = useGetState<{
    errorName?: MassageRuntime.RobotExceptionEvents;
    points: Palpation.PointTypeWithDirection[];
    barePosition?: BarePositionType;
    isLightAbnormal: boolean;
  }>({
    isLightAbnormal: false,
    points: [],
  });
  const currentErrorNameRef = useRef<null | MassageRuntime.RobotExceptionEvents>();
  const [isConfirmingPalpationResult, setIsConfirmingPalpationResult] = useState(false);
  const [isVideoPlay, setIsVideoPlay] = useState(false);
  const [massageSettingsOptions, setMassageSettingsOptions] = useState<GetMassageSettingsOptionsResponse>([]);
  const [imgUrl, setImgUrl, getImgUrl] = useGetState(
    ''
    // 'https://massage-proxy-7554-80.apps-hp.danlu.netease.com/saas/image/shuyuansdk_share%3Amassage-prod/18e6d32da4cf4b78aa94b811027dcf83/2023412_1681301860_661724de9a5e4d57af0f69ae61ee91cf.png'
  );
  const [helperPoints, setHelperPoints, getHelperPoints] = useGetState<number[]>();
  const [drawerScreenPic, setDrawerScreenPic, getDrawerScreenPic] = useGetState<string>('');
  const currentServingOrder = useSelector(selectCurrentServingOrder);
  const [isRetryingDetecting, setIsRetryingDetecting] = useState(false);
  const [noduleHitsNum, setNoduleHitsNum] = useState(null);
  const body2DViewerRef = useRef<Body2DViewerRef>(null);
  const humanDetectionRef = useRef<HumanDetectionRef>(null);
  const isClickedConfirmedBtn = useRef(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [currentStep, setCurrentStep] = useState<Service.SubStep>(null);
  const [isDetectedError, setIsDetectedError] = useState(false);
  const [bodyDetectAbnormalImg, setBodyDetectAbnormalImg] = useState('');
  const [isClickedEnd, setIsClickedEnd] = useState(false);
  const [massageConfigInfo, setMassageConfigInfo] = useState<MassageConfigInfoType>({
    'massage.tech': null,
    'massage.nodules.duration': null,
  });
  const [isDepthWarningDrawerShow, setIsDepthWarningDrawerShow] = useState(false);
  const [isHandlingToSetting, setIsHandlingToSetting] = useState(false);
  const [customRouteList, setCustomRouteList] = useState<GetPalpationDatResponse['palpationData']['custom_routes']>([]);
  const isDrawDottedMask = useRef(false);
  // 视频第二段tips显示
  const [showFirstTips, setShowFirstTips] = useState<boolean>(true);
  const [isGetSettingOptionLoading, setIsGetSettingOptionLoading] = useState<boolean>(true);

  const uniqueLeftPoints = useMemo(
    () =>
      leftPoints?.filter((item, index, self) => self.findIndex(item2 => item2.noduleId === item.noduleId) === index),
    [leftPoints]
  );

  const uniqueRightPoints = useMemo(
    () =>
      rightPoints?.filter((item, index, self) => self.findIndex(item2 => item2.noduleId === item.noduleId) === index),
    [rightPoints]
  );

  const renderPoints = useMemo(
    () => uniqueLeftPoints.slice(0).concat(uniqueRightPoints),
    [uniqueLeftPoints, uniqueRightPoints]
  );

  // 冲击波结节打击次数设置枚举值
  const shockNoduleHitsValues = useMemo(
    () =>
      massageSettingsOptions
        ?.find(item => item.massageItem === 'Shockwave' && item.massageType === 'Massage')
        ?.controlItemConfigs?.find(item => item.name === 'shockwave.nodule_hits')?.values,
    [massageSettingsOptions]
  );

  // 按摩方案手法设置枚举值
  const massageTechSettingValues = useMemo(
    () =>
      massageSettingsOptions
        ?.find(item => item.massageItem === 'Massage' && item.massageType === 'Massage')
        ?.controlItemConfigs?.find(item => item.name === 'massage.tech')?.values,
    [massageSettingsOptions]
  );

  // 按摩手法时间设置枚举值
  const massageDurationSettingValues = useMemo(
    () =>
      massageSettingsOptions
        ?.find(item => item.massageItem === 'Massage' && item.massageType === 'Massage')
        ?.controlItemConfigs?.find(item => item.name === 'massage.nodules.duration')?.values,
    [massageSettingsOptions]
  );

  // 检测到人体
  const isDetectedHuman = useMemo(() => massageState === 'Preparing_AcupointsAnalyzing', [massageState]);

  // 检测超时
  const isDetectingTimeout = useMemo(() => massageState === 'Preparing_HumanDetectingTimeout', [massageState]);

  // 是否是自定义方案画线
  const isCustomDrawLinePlan = useMemo(
    () =>
      currentServingOrder?.stepFlow?.find(item => item.step === 'RoboticTask')?.subStep?.includes('CustomPalpation'),
    [currentServingOrder]
  );

  // 回显已保存的数据
  const getPalpationResult = useCallback(async () => {
    const data = await socketInstance.current?.getPalpationData();

    const formatData = convertObjectKeysToCamelCase(data);
    data?.image && setImgUrl(data?.image);

    if (data?.helper_points && !getHelperPoints()?.length) {
      setHelperPoints(data?.helper_points);
    }

    if (data?.palpation_data?.custom_routes?.length) {
      setCustomRouteList(data.palpation_data?.custom_routes);
    }

    const leftPoints = formatData?.palpationData?.noduleSet?.left;
    const rightPoints = formatData?.palpationData?.noduleSet?.right;

    leftPoints && setLeftPoints(leftPoints.map(item => ({ ...item, isLeft: true })));
    rightPoints && setRightPoints(rightPoints.map(item => ({ ...item, isLeft: false })));
  }, [getHelperPoints, setHelperPoints, setImgUrl, socketInstance]);

  // 初始化手诊事件
  // eslint-disable-next-line react-func/max-lines-per-function
  const initPalpationEvents = useCallback(() => {
    PalpationEventBus.on('palpation_data', event => {
      const palpationData = event.details.data;
      const formatIncrData = convertObjectKeysToCamelCase(palpationData);

      // 增加痛点
      if (formatIncrData.noduleSet) {
        if (formatIncrData.noduleSet?.left?.length) {
          setLeftPoints(prevValue => [
            ...prevValue,
            ...formatIncrData.noduleSet?.left
              ?.filter(item => !prevValue.includes(item.noduleId))
              .map(item => ({ ...item, isLeft: true })),
          ]);
        }
        if (formatIncrData.noduleSet?.right?.length) {
          setRightPoints(prevValue => [
            ...prevValue,
            ...formatIncrData.noduleSet?.right
              ?.filter(item => !prevValue.includes(item.noduleId))
              ?.map(item => ({ ...item, isLeft: false })),
          ]);
        }
      }
    });

    PalpationEventBus.on('palpation_image', event => {
      console.log('拿到人体图片', event);
      // 关闭人体检测异常Modal
      setIsDetectedError(false);

      getImgUrl() || setImgUrl(event.details.image);
      event?.details?.helper_points && setHelperPoints(event.details.helper_points);
    });

    // 人体检测异常
    PalpationEventBus.on('error_fire', (event: { details: BodyAnomalyDetectionResponse }) => {
      const { error: currentErrorName, coord, error_image, is_light_abnormal } = event.details;
      let points: Palpation.PointTypeWithDirection[] = [];
      let barePosition;

      setBodyDetectAbnormalImg(error_image);
      setIsDetectedError(true);
      // 恢复视频字幕默认值从第一句开始播放
      setShowFirstTips(true);
      // 裸背异常检测还需渲染左侧视图
      if (BareAreaAbnormalErrorList.includes(currentErrorName)) {
        barePosition = coord?.bare_position;
      }

      // 其他情况直接在右侧渲染异常点
      points = coord?.pixel_position.map(({ x, y }, index) => {
        return {
          pixelPosition: { x, y },
          noduleId: `${index + 1}`,
          index: index + 1,
          isLeft: false,
          depth_warning: false,
        };
      });

      currentErrorNameRef.current = currentErrorName;
      setBodyDetectAbnormalPoints({
        points,
        errorName: currentErrorName,
        barePosition,
        isLightAbnormal: is_light_abnormal,
      });
    });

    // 触发时关闭所有人体异常检测提示
    PalpationEventBus.on('error_done', (event: { details: BodyAnomalyDetectionResponse }) => {
      const errorRecoverName = event.details.error;

      if (getBodyDetectAbnormalPoints()?.errorName === errorRecoverName) {
        setIsDetectedError(false);
      }
    });

    PalpationEventBus.on('knots_limited_warning', () => {
      Toast.show('痛点数量已达标注数量上限');
    });

    PalpationEventBus.on('state_change', event => {
      const state = event.details?.state;

      // 穴位识别、标记结节
      if (
        ['Preparing_AcupointsAnalyzing', 'Preparing_KnotsMarking', 'Preparing_AcupointsCalibration'].includes(state)
      ) {
        setIsDetectedError(false);
      }

      if (
        ['Preparing_HumanDetecting', 'Preparing_AcupointsAnalyzing', 'Preparing_HumanDetectingTimeout'].includes(state)
      ) {
        humanDetectionRef.current?.start();
      }
    });
  }, [getBodyDetectAbnormalPoints, getImgUrl, setBodyDetectAbnormalPoints, setHelperPoints, setImgUrl]);

  const init = useCallback(async () => {
    initPalpationEvents();

    const step = await socketInstance.current.getCurrentStep();
    setCurrentStep(step?.step);
    const taskInfo = await socketInstance.current?.getMachineState();

    // 当前是标记痛点或自定义画线流程，开始获取痛点和图片信息
    if (
      [
        'Preparing_KnotsMarking',
        'Preparing_WaitingDrawCustomRoute',
        'Preparing_DrawCustomRoute',
        'Preparing_WaitingTechRouteGenerating',
        'Preparing_Setting',
        'Preparing_AcupointsCalibration',
      ].includes(taskInfo?.state)
    ) {
      getPalpationResult();
    } else if (
      ['Preparing_HumanDetecting', 'Preparing_AcupointsAnalyzing', 'Preparing_HumanDetectingTimeout'].includes(
        taskInfo?.state
      )
    ) {
      humanDetectionRef.current?.start();
    }
    setIsGetSettingOptionLoading(true);
    // 通过接口数据设置默认值
    const { data: options } = await service.massage.getMassageSettingsOptions(currentServingOrder?.orderId);
    setIsGetSettingOptionLoading(false);
    setMassageSettingsOptions(convertObjectKeysToCamelCase(options));
  }, [currentServingOrder?.orderId, getPalpationResult, initPalpationEvents, socketInstance]);

  const onSortEnd = useCallback((part, points: Palpation.PointType[]) => {
    if (part === 'left') {
      setLeftPoints(points.map(item => ({ ...item, isLeft: true })));
    } else {
      setRightPoints(points.map(item => ({ ...item, isLeft: false })));
    }
  }, []);

  // 删除痛点
  const onDeleteNodule = useCallback(
    async (noduleId, cb) => {
      !!noduleId && (await socketInstance.current.deleteNodule({ nodule_id: noduleId }));
      cb?.();
    },
    [socketInstance]
  );

  /** 上传手诊截图图片 */
  const handleUploadScreenshot = useCallback(async () => {
    if (!body2DViewerRef.current) {
      console.log('画布未加载完成');
      return;
    }
    try {
      const base64ImgString = body2DViewerRef.current?.screenshot();
      if (!base64ImgString) {
        console.log('未获取到图片');
        return;
      }
      const screenshot = await uploadBase64Img(base64ImgString);
      console.log('上传完成图片', screenshot);
      return screenshot;
    } catch (e) {
      console.error('上传图片失败', e);
      return '';
    }
  }, []);

  // 确认手诊结果
  const handleConfirmPalpationResult = useCallback(
    ({ isSkip = false }) =>
      async () => {
        // 自定义画线流程跳过去到设置页面
        if (isCustomDrawLinePlan && isSkip) {
          try {
            setIsConfirmingPalpationResult(true);

            await socketInstance.current.deleteNodule({
              nodule_id: uniqueLeftPoints
                ?.map(item => item.noduleId)
                ?.concat(uniqueRightPoints?.map(item => item.noduleId))
                ?.flat(),
            });

            const screenshot = await handleUploadScreenshot();

            const palpationData: Palpation.PalpationDataType = {
              forbidArea: [],
              noduleSet: {
                part: (currentServingOrder?.bodyPart || '') as Service.BodyPart,
                left: [],
                right: [],
              },
            };

            const data = await socketInstance.current.getPalpationData();

            palpationData.customRoutes = convertObjectKeysToCamelCase(data)?.palpationData?.customRoutes;

            await service.palpation.savePalpation({
              serviceOrderId: currentServingOrder?.orderId,
              screenshot,
              palpationData,
            });

            setLeftPoints([]);
            setRightPoints([]);
          } finally {
            setIsConfirmingPalpationResult(false);
          }
          return;
        }

        if (isClickedConfirmedBtn.current) {
          return;
        }
        isClickedConfirmedBtn.current = true;

        const palpationData: Palpation.PalpationDataType = {
          forbidArea: [],
          noduleSet: {
            part: (currentServingOrder?.bodyPart || '') as Service.BodyPart,
            left: isSkip ? [] : uniqueLeftPoints,
            right: isSkip ? [] : uniqueRightPoints,
          },
        };
        try {
          setIsConfirmingPalpationResult(true);
          const screenshot = await handleUploadScreenshot();

          if (currentServingOrder?.productItems[0] === 'Shockwave') {
            await service.palpation.savePalpation({
              serviceOrderId: currentServingOrder?.orderId,
              noduleHits: noduleHitsNum,
              screenshot,
              palpationData,
            });
          }

          if (currentServingOrder?.productItems[0] === 'Massage') {
            await service.palpation.savePalpation({
              serviceOrderId: currentServingOrder?.orderId,
              screenshot,
              palpationData,
              noduleDuration: massageConfigInfo['massage.nodules.duration'],
              techId: massageConfigInfo['massage.tech'],
            });
          }

          onSuccessPalpation?.();
        } catch (e) {
          isClickedConfirmedBtn.current = false;
        } finally {
          setIsConfirmingPalpationResult(false);
        }
      },
    [
      currentServingOrder,
      handleUploadScreenshot,
      isCustomDrawLinePlan,
      massageConfigInfo,
      noduleHitsNum,
      onSuccessPalpation,
      socketInstance,
      uniqueLeftPoints,
      uniqueRightPoints,
    ]
  );

  // 重试人体检测
  const handleRetryHumanDetecting = useCallback(async () => {
    setIsRetryingDetecting(true);
    try {
      await service.lanInterface.retryHumanDetecting(currentServingOrder?.deviceId);
      setBodyDetectAbnormalPoints({ isLightAbnormal: false, points: [] });
      setIsDetectedError(false);
    } finally {
      setIsRetryingDetecting(false);
    }
  }, [currentServingOrder?.deviceId, setBodyDetectAbnormalPoints]);

  // 自定义画线去设置理疗参数
  const handleToSetting = useMemoizedFn(async () => {
    try {
      setIsHandlingToSetting(true);
      const screenshot = await handleUploadScreenshot();

      const palpationData: Palpation.PalpationDataType = {
        forbidArea: [],
        noduleSet: {
          part: (currentServingOrder?.bodyPart || '') as Service.BodyPart,
          left: uniqueLeftPoints,
          right: uniqueRightPoints,
        },
      };

      const data = await socketInstance.current.getPalpationData();

      palpationData.customRoutes = convertObjectKeysToCamelCase(data)?.palpationData?.customRoutes;

      await service.palpation.savePalpation({
        serviceOrderId: currentServingOrder?.orderId,
        screenshot,
        palpationData,
      });
    } finally {
      setIsHandlingToSetting(false);
    }
  });

  useUpdateEffect(() => {
    if (!isDepthWarningDrawerShow) {
      URL.revokeObjectURL(drawerScreenPic);
    }
  }, [isDepthWarningDrawerShow]);

  useUnmount(() => {
    URL.revokeObjectURL(getDrawerScreenPic());
  });

  useEffect(() => {
    if (isSocketConnected && currentServingOrder?.orderId) {
      init();
      return () => {
        PalpationEventBus.removeAllListeners();
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSocketConnected, currentServingOrder?.orderId]);

  // 设置默认值
  useEffect(() => {
    if (massageSettingsOptions?.length) {
      const massageInfo: MassageConfigInfoType = {} as any;

      massageSettingsOptions.forEach(item => {
        if (item.massageType === 'Massage') {
          // 回显冲击波默认打击次数
          if (item.massageItem === 'Shockwave') {
            setNoduleHitsNum(
              item.controlItemConfigs.find(item2 => item2.name === 'shockwave.nodule_hits')?.default?.value
            );
          }

          // 回显按摩数据
          if (item.massageItem === 'Massage') {
            item.controlItemConfigs?.forEach(item2 => {
              if (item2.name === 'massage.tech') {
                massageInfo['massage.tech'] = +item2.default.value;
              } else if (item2.name === 'massage.nodules.duration') {
                massageInfo['massage.nodules.duration'] = +item2.default.value;
              }
            });
          }
        }
      });

      setMassageConfigInfo(massageInfo);
    }
  }, [massageSettingsOptions]);

  useEffect(() => {
    // 手诊页面打击总次数 = 计算总痛点个数 * 单个痛点打击次数+ 走线总次数
    const noduleHitsTotalCount = lineHits + noduleHitsNum * (leftPoints?.length + rightPoints?.length);
    if (noduleHitsTotalCount >= 25000) {
      Toast.show('本次方案打击次数已达到25000次，建议调整打击次数，以免影响设备寿命');
    }
  }, [noduleHitsNum, leftPoints?.length, rightPoints?.length, lineHits]);

  // 弹窗打开时，实时更新结节映射图
  useEffect(() => {
    let timer = null;
    if (isDepthWarningDrawerShow) {
      timer = setTimeout(() => {
        try {
          const base64ImgString = body2DViewerRef.current?.screenshot();
          const imgUrl = base64String2ObjectURL(base64ImgString);
          setDrawerScreenPic(imgUrl);
        } catch (e) {
          console.log('截图失败', e);
        }
      }, 300);
    }
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDepthWarningDrawerShow, renderPoints]);

  // 渲染可画线/点区域
  useEffect(() => {
    if (isImageLoaded && helperPoints?.length) {
      if (massageState !== 'Preparing_AcupointsCalibration' && !isDrawDottedMask.current) {
        // 将第一个坐标添加至尾部形成图形闭环
        body2DViewerRef.current?.updateDottedLinePoints([...helperPoints, helperPoints?.[0], helperPoints?.[1]]);
        isDrawDottedMask.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImageLoaded, helperPoints?.length, massageState]);

  // 根据数据动态自定义画线图形
  useEffect(() => {
    if (!isImageLoaded) return;

    body2DViewerRef.current?.deleteAllDottedLineCircle();
    if (customRouteList?.length) {
      customRouteList.forEach(item => {
        if (item.state === 'Error') {
          body2DViewerRef.current?.drawDottedCircleLine(item?.visual_coordinates?.flat(), {
            lineId: item.id,
            stroke: CUSTOM_LINE_COLOR.Error,
          });
        } else {
          // 只在画线阶段渲染视觉的线
          [
            'Preparing_WaitingDrawCustomRoute',
            'Preparing_DrawCustomRoute',
            'Preparing_WaitingTechRouteGenerating',
          ].includes(massageState) &&
            item?.visual_coordinates?.length &&
            body2DViewerRef.current?.drawDottedCircleLine(item?.visual_coordinates?.flat(), {
              lineId: item.id,
              stroke: CUSTOM_LINE_COLOR.Detected,
            });
          item?.tech_coordinates?.length &&
            body2DViewerRef.current?.drawDottedCircleLine(item?.tech_coordinates?.flat(), {
              lineId: item.id,
              stroke: CUSTOM_LINE_COLOR.Generated,
              opacity: ['Preparing_KnotsMarking', 'Preparing_Setting'].includes(massageState) ? 0.5 : 1,
            });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImageLoaded, customRouteList, massageState]);

  return (
    <div className={cx('palpation')}>
      {currentStep !== 'Massage' && <ServiceProcessCrumb className={cx('crumb')} />}
      <div className={cx('main-content', { 'massage-step': currentStep === 'Massage' })}>
        <div className={cx('left')}>
          {imgUrl ? (
            <Body2DViewer
              shouldRenderOpacityNodule={isDepthWarningDrawerShow}
              ref={body2DViewerRef}
              points={renderPoints}
              hasCustomRouteLine={!!customRouteList?.length}
              imgUrl={imgUrl}
              onImageLoaded={() => setTimeout(() => setIsImageLoaded(true), 1000)}
            />
          ) : (
            <HumanDetection
              isDetectingTimeout={isDetectingTimeout}
              barePosition={bodyDetectAbnormalPoints.barePosition}
              errorName={bodyDetectAbnormalPoints.errorName}
              socketInstance={socketInstance}
              ref={humanDetectionRef}
              isDetectedHuman={isDetectedHuman}
              headerText={
                currentServingOrder?.productItems?.includes('Shockwave') && currentStep === 'VisualCheckAndPalpation'
                  ? '手诊影像采集框'
                  : '人体检测'
              }
              showVideoWrapper={massageState !== 'Preparing_KnotsMarking' && isVideoPlay}
              onToggleVideoPlayState={setIsVideoPlay}
              currentStep={currentStep}
            />
          )}
        </div>
        <div className={cx('right')}>
          {/* 人体检测异常 */}
          {isDetectedError ? (
            <AnomalyDetectCard
              isLightAbnormal={bodyDetectAbnormalPoints.isLightAbnormal}
              barePosition={bodyDetectAbnormalPoints.barePosition}
              endService={endService}
              currentStep={currentStep}
              handleRetryHumanDetecting={handleRetryHumanDetecting}
              isDetectingTimeout={isDetectingTimeout}
              handleClose={() => setIsDetectedError(false)}
              part={currentServingOrder?.bodyPart}
              points={bodyDetectAbnormalPoints.points}
              currentErrorName={bodyDetectAbnormalPoints.errorName}
              imgUrl={bodyDetectAbnormalImg}
              onImageLoaded={() => {}}
            />
          ) : // 穴位矫正
          massageState === 'Preparing_AcupointsCalibration' ? (
            <AcupointCalibration
              isSocketConnected={isSocketConnected}
              socketInstance={socketInstance}
              body2DViewerRef={body2DViewerRef}
              isImageLoaded={isImageLoaded}
            />
          ) : // 标记结节
          massageState === 'Preparing_KnotsMarking' ? (
            <div className={cx('tubercle-box')}>
              <div className={cx('tubercle-area', { 'is-custom-line': isCustomDrawLinePlan })}>
                <div className={cx('point-box')}>
                  <div className={cx('side', 'left-side')}>
                    <div className={cx('name')}>
                      <div className={cx('circle')} />
                      <span>左侧</span>
                    </div>
                    <When condition={!!uniqueLeftPoints?.concat(uniqueRightPoints)?.length}>
                      <DragContent
                        onDeleteNodule={onDeleteNodule}
                        className={cx('drag-content', 'left-content')}
                        points={uniqueLeftPoints}
                        onChange={points => onSortEnd('left', points)}
                      />
                    </When>
                  </div>
                  <div className={cx('side', 'right-side')}>
                    <div className={cx('name')}>
                      <div className={cx('circle', 'circle-green')} />
                      <span>右侧</span>
                    </div>
                    <When condition={!!uniqueLeftPoints?.concat(uniqueRightPoints)?.length}>
                      <DragContent
                        onDeleteNodule={onDeleteNodule}
                        className={cx('drag-content', 'right-content')}
                        points={uniqueRightPoints}
                        onChange={points => onSortEnd('right', points)}
                      />
                    </When>
                  </div>
                  <When condition={!uniqueLeftPoints?.concat(uniqueRightPoints)?.length}>
                    <div className={cx('empty-guide')}>
                      <div className={cx('tips')}>
                        <Icon name="info-circle" color="#000000A6" className={cx('tips-icon')} size={18} />
                        <div>请根据标准手势标记痛点</div>
                      </div>
                      <div className={cx('tips-img')}>
                        <video poster={GUIDE_VIDEO_POSTER} autoPlay loop muted src={NODULE_GUIDE_VIDEO} />
                      </div>
                    </div>
                  </When>
                </div>
                {!!uniqueLeftPoints?.concat(uniqueRightPoints)?.length && !isCustomDrawLinePlan && (
                  <>
                    {currentServingOrder?.productItems?.[0] === 'Shockwave' ? (
                      <div className={cx('nodule-box')}>
                        <div className={cx('label')}>单个痛点打击次数</div>
                        <div>
                          <Stepper
                            suffix="次"
                            value={noduleHitsNum}
                            onChange={setNoduleHitsNum}
                            {...genMinMaxSteps(shockNoduleHitsValues)}
                          />
                        </div>
                      </div>
                    ) : currentServingOrder?.productItems?.[0] === 'Massage' ? (
                      <div className={cx('massage-item-setting-box')}>
                        {!!massageTechSettingValues?.length && (
                          <div className={cx('nodule-box')}>
                            <div className={cx('label')}>手法</div>
                            <div>
                              <SlideSelect
                                activeColor="white"
                                steps={massageTechSettingValues}
                                value={massageConfigInfo['massage.tech']}
                                onChange={value =>
                                  setMassageConfigInfo(prev => {
                                    const backupValues = klona(prev);
                                    backupValues['massage.tech'] = +value;
                                    return backupValues;
                                  })
                                }
                              />
                            </div>
                          </div>
                        )}

                        <div className={cx('nodule-box')}>
                          <Skeleton loading={isGetSettingOptionLoading} paragraph={{ rows: 1 }} title={false}>
                            <div className={cx('label')}>单个痛点按摩时间</div>
                            <div>
                              <Stepper
                                suffix="秒"
                                value={massageConfigInfo['massage.nodules.duration']}
                                onChange={value =>
                                  setMassageConfigInfo(prev => {
                                    const backupValues = klona(prev);
                                    backupValues['massage.nodules.duration'] = value;
                                    return backupValues;
                                  })
                                }
                                {...genMinMaxSteps(massageDurationSettingValues)}
                              />
                            </div>
                          </Skeleton>
                        </div>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
              <div className={cx('footer')}>
                <Button
                  loading={isConfirmingPalpationResult}
                  className={cx('cancel-btn')}
                  disabled={
                    currentServingOrder?.deviceId?.includes('Simulate')
                      ? false
                      : isDisconnectMachineArm || !isImageLoaded
                  }
                  onClick={handleConfirmPalpationResult({ isSkip: true })}
                >
                  跳过
                </Button>
                {isCustomDrawLinePlan ? (
                  <Button
                    color="primary"
                    disabled={
                      isDisconnectMachineArm ||
                      uniqueLeftPoints?.length + uniqueRightPoints?.length === 0 ||
                      !isImageLoaded
                    }
                    className={cx('btn', 'confirm-btn')}
                    loading={isHandlingToSetting}
                    onClick={() => {
                      // 当前有滑落风险的结节
                      if (renderPoints?.some(item => item.depthWarning)) {
                        setIsDepthWarningDrawerShow(true);
                        return;
                      }
                      handleToSetting();
                    }}
                  >
                    设定理疗参数
                  </Button>
                ) : (
                  <Button
                    color="primary"
                    loading={isConfirmingPalpationResult}
                    className={cx('btn', 'confirm-btn', {
                      'show-action-point':
                        !isConfirmingPalpationResult && uniqueLeftPoints?.length + uniqueRightPoints?.length !== 0,
                    })}
                    disabled={
                      isDisconnectMachineArm ||
                      uniqueLeftPoints?.length + uniqueRightPoints?.length === 0 ||
                      !isImageLoaded ||
                      (currentServingOrder?.productItems?.[0] === 'Massage' && !massageDurationSettingValues?.length) // 修复有时候因网络不好，单个痛点按摩时间未获取到却可以点击下一步的情况
                    }
                    onClick={() => {
                      // 当前有滑落风险的结节
                      if (renderPoints?.some(item => item.depthWarning)) {
                        setIsDepthWarningDrawerShow(true);
                        return;
                      }
                      handleConfirmPalpationResult({ isSkip: false })();
                    }}
                  >
                    确认手诊结果
                  </Button>
                )}
              </div>
            </div>
          ) : // 人体检测/穴位识别/检测超时
          ['Preparing_HumanDetecting', 'Preparing_AcupointsAnalyzing', 'Preparing_HumanDetectingTimeout']?.includes(
              massageState
            ) ? (
            <div className={cx('no-confirm-box')}>
              {!isDetectingTimeout && (
                <div className={cx('header')}>
                  <div className={cx('title')}>人体检测中</div>
                  <div className={cx('tips-wrapper')}>
                    <LoadingOutlined className={cx('icon')} />
                    <span>整个检测过程会持续20秒左右</span>
                  </div>
                </div>
              )}
              {isDetectingTimeout ? (
                <>
                  <div className={cx('detecting-fail')}>人体检测失败，请再次检测</div>
                  <div className={cx('retry-box')}>
                    <Button
                      disabled={isDisconnectMachineArm}
                      onClick={async () => {
                        setIsClickedEnd(true);
                        const isConfirm = await endService?.();
                        !isConfirm && setIsClickedEnd(false);
                      }}
                      loading={isStoping}
                    >
                      结束服务
                    </Button>
                    <Button
                      loading={isRetryingDetecting}
                      disabled={isDisconnectMachineArm || isClickedEnd}
                      color="primary"
                      className={cx('retry-btn')}
                      onClick={handleRetryHumanDetecting}
                    >
                      再次检测
                    </Button>
                  </div>
                </>
              ) : isDetectedHuman ? (
                <>
                  <div className={cx('detecting-loading-text')}>人体扫描完成，理疗线路生成中...</div>
                  <FrameAnimation
                    className={cx('detecting-loading')}
                    url={storagePrefixFixedV2 + '/human-body-detection/video-sequence-frame/detecting-loading.png'}
                    aspectRatio={2.298}
                    count={54}
                    duration={1}
                    width={170}
                  />
                </>
              ) : (
                <>
                  <div className={cx('video-wrapper')}>
                    <div className={cx('tips-text-wrapper')}>
                      <AnimatePresence mode="wait">
                        {showFirstTips ? (
                          <motion.div
                            key="first-video-tips"
                            initial={{ opacity: 1 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2, delay: 0.1, ease: 'easeInOut' }}
                            className={cx('video-tips-text')}
                          >
                            1.{VIDEO_COPYWRITING_DESC[currentServingOrder?.bodyPart]?.firstTips}
                          </motion.div>
                        ) : (
                          <motion.div
                            key="second-video-tips"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2, delay: 0.1, ease: 'easeInOut' }}
                            className={cx('video-tips-text')}
                          >
                            2.{VIDEO_COPYWRITING_DESC[currentServingOrder?.bodyPart]?.secondTips}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    <div className={cx('video')}>
                      <video
                        muted
                        loop
                        autoPlay
                        poster={VIDEO_COPYWRITING_DESC[currentServingOrder?.bodyPart]?.poster}
                        src={VIDEO_COPYWRITING_DESC[currentServingOrder?.bodyPart]?.src}
                        onTimeUpdate={event =>
                          setShowFirstTips(
                            //@ts-ignore
                            event.target.currentTime <= VIDEO_COPYWRITING_DESC[currentServingOrder?.bodyPart]?.tipsTime
                          )
                        }
                      />
                    </div>
                  </div>
                  <SuggestionsForFeedback
                    currentProcess={
                      currentStep === 'Massage' && massageState === 'Preparing_HumanDetecting'
                        ? 'massage-visual-check'
                        : 'visual-check'
                    }
                    className={cx('feedback-wrapper')}
                  />
                </>
              )}
            </div>
          ) : [
              'Preparing_WaitingDrawCustomRoute',
              'Preparing_DrawCustomRoute',
              'Preparing_WaitingTechRouteGenerating',
              'Preparing_Setting',
            ].includes(massageState) ? (
            <CustomDrawLine
              isSocketConnected={isSocketConnected}
              body2DViewerRef={body2DViewerRef}
              socketInstance={socketInstance}
              isImageLoaded={isImageLoaded}
              customRouteList={customRouteList}
              setCustomRouteList={setCustomRouteList}
              onSuccessPalpation={onSuccessPalpation}
            />
          ) : null}
        </div>
      </div>
      <AnomalyDepthDrawer
        isSocketConnected={isSocketConnected}
        open={isDepthWarningDrawerShow}
        setOpen={setIsDepthWarningDrawerShow}
        screenPic={drawerScreenPic}
        points={renderPoints}
        confirmLoading={isConfirmingPalpationResult}
        delWarningPointCb={() => {
          return new Promise((resolve, reject) => {
            socketInstance.current
              .deleteNodule({ nodule_id: renderPoints?.filter(item => item.depthWarning)?.map(item => item.noduleId) })
              .then(() => {
                setLeftPoints(prev => prev?.filter(item => !item.depthWarning));
                setRightPoints(prev => prev?.filter(item => !item.depthWarning));
                setIsDepthWarningDrawerShow(false);
                resolve('');
              })
              .catch(e => {
                reject(e);
              });
          });
        }}
        confirmKeepCb={() => {
          if (isCustomDrawLinePlan) {
            setIsDepthWarningDrawerShow(false);
            handleToSetting();
          } else {
            handleConfirmPalpationResult({ isSkip: false })();
          }
        }}
      />
    </div>
  );
};

export default Palpation;
